import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, FileText, Megaphone, CalendarRange, Pause, Play, Trash2, ExternalLink, PenLine, Search, Globe } from "lucide-react";
import ArticlePreview from "@/components/content/ArticlePreview";
import { PPCPlanView } from "@/components/content/PPCModal";
import { useContentDefaults } from "@/components/content/DraftBuilderModal";

export const STATUS_CLS = {
  scheduled: "bg-sky-500/15 text-sky-300 border-sky-500/30", published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  failed: "bg-rose-500/15 text-rose-300 border-rose-500/30", draft: "bg-secondary text-foreground border-border",
  paused: "bg-amber-500/15 text-amber-300 border-amber-500/30", paused_billing_issue: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  ready: "bg-violet-500/15 text-violet-300 border-violet-500/30",
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric" }) : "—");

const ItemRow = ({ it, onOpen }) => (
  <button onClick={() => onOpen(it)} className="w-full flex items-center gap-3 px-3 py-2.5 border-t border-border text-left hover:bg-secondary/20 transition-colors" data-testid={`content-item-${it.id}`}>
    <span className="text-xs font-mono text-muted-foreground w-14 shrink-0">{fmtDate(it.scheduled_publish_date || it.created_at)}</span>
    {it.format_type === "ppc_ad" ? <Megaphone className="h-4 w-4 text-violet-300 shrink-0" /> : <FileText className="h-4 w-4 text-primary shrink-0" />}
    <span className="flex-1 min-w-0">
      <span className="block text-sm truncate">{it.article_title || it.target_keyword}</span>
      <span className="block text-[11px] text-muted-foreground truncate">{it.target_keyword}{it.angle ? ` · ${it.angle}` : ""}{it.error ? ` · ${it.error}` : ""}</span>
    </span>
    <Badge className={`border text-[10px] shrink-0 ${STATUS_CLS[it.status] || STATUS_CLS.draft}`}>{(it.status || "").replace(/_/g, " ")}</Badge>
  </button>
);

function CampaignCard({ c, expanded, onToggle, onChanged, onOpen }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (expanded) api.get(`/content-campaigns/${c.id}`).then((r) => setDetail(r.data)).catch(() => {}); }, [expanded, c.id]);
  const act = async (a) => {
    try { a === "delete" ? await api.delete(`/content-campaigns/${c.id}`) : await api.post(`/content-campaigns/${c.id}/${a}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const n = c.counts || {};
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`content-campaign-${c.id}`}>
      <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
        <button onClick={onToggle} className="text-left flex-1 min-w-0">
          <p className="font-semibold flex items-center gap-2"><CalendarRange className="h-4 w-4 text-primary" /> {c.months}-month blog campaign · {c.days} articles</p>
          <p className="text-xs text-muted-foreground mt-1 truncate">{c.keywords.join(", ")}</p>
          <p className="text-xs mt-2 flex flex-wrap gap-3">
            <span className="text-emerald-300">{n.published || 0} published</span><span className="text-sky-300">{n.scheduled || 0} scheduled</span>
            {n.failed ? <span className="text-rose-300">{n.failed} failed</span> : null}
            <span className="text-muted-foreground">{[c.publish_shopify && "Shopify", c.publish_inapp && "Blog page", c.promote_social && "FB + LinkedIn promo", c.include_ppc && "PPC"].filter(Boolean).join(" · ")}</span>
          </p>
        </button>
        <div className="flex gap-1.5">
          {c.status === "active"
            ? <Button size="sm" variant="outline" className="border-border h-8" onClick={() => act("pause")} data-testid={`campaign-pause-${c.id}`}><Pause className="h-3.5 w-3.5" /></Button>
            : <Button size="sm" variant="outline" className="border-border h-8" onClick={() => act("resume")} data-testid={`campaign-resume-${c.id}`}><Play className="h-3.5 w-3.5" /></Button>}
          <Button size="sm" variant="outline" className="border-border h-8 text-rose-300" onClick={() => act("delete")} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {expanded && (!detail ? <div className="p-4"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
        <div className="max-h-[420px] overflow-y-auto">
          {detail.ppc && <div className="p-4 border-t border-border"><PPCPlanView doc={detail.ppc} /></div>}
          {detail.items.map((it) => <ItemRow key={it.id} it={it} onOpen={onOpen} />)}
        </div>
      ))}
    </div>
  );
}

function ItemDialog({ item, onClose, shopifyConnected }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFull(null); if (item && item.format_type !== "ppc_ad") api.get(`/content/items/${item.id}`).then((r) => setFull(r.data)).catch(() => {}); }, [item]);
  const writeNow = async () => {
    setBusy(true);
    try { setFull((await api.post(`/content/items/${item.id}/prepare`)).data); toast.success("Article written"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not write the article"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[92vh] overflow-y-auto" data-testid="content-item-dialog">
        <DialogHeader><DialogTitle className="font-display">{item?.article_title || item?.target_keyword}</DialogTitle></DialogHeader>
        {!full ? <Loader2 className="h-5 w-5 animate-spin" /> : full.rendered_html
          ? <ArticlePreview item={full} shopifyConnected={shopifyConnected} onChange={setFull} />
          : (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Scheduled for {new Date(full.scheduled_publish_date).toLocaleString()}. It is written automatically 24 hours before publishing.</p>
              <Button onClick={writeNow} disabled={busy} className="bg-primary hover:bg-primary/90 text-white" data-testid="content-write-now-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />} Write it now
              </Button>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

export default function ContentHub() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const defaults = useContentDefaults(active?.id);
  const [camps, setCamps] = useState(null);
  const [items, setItems] = useState([]);
  const [ppcs, setPpcs] = useState([]);
  const [expanded, setExpanded] = useState(params.get("campaign"));
  const [open, setOpen] = useState(null);
  const load = () => {
    if (!active) return;
    api.get(`/content-campaigns?workspace_id=${active.id}`).then((r) => setCamps(r.data)).catch(() => setCamps([]));
    api.get(`/content/items?workspace_id=${active.id}`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/ppc?workspace_id=${active.id}`).then((r) => setPpcs(r.data)).catch(() => {});
  };
  useEffect(load, [active]); // eslint-disable-line
  const Empty = ({ text }) => (
    <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
      {text} <Button size="sm" variant="outline" className="ml-2 border-border" onClick={() => navigate("/keywords")} data-testid="hub-go-keywords"><Search className="h-3.5 w-3.5 mr-1.5" /> Keyword Research</Button>
    </div>
  );
  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="content-hub-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Blogs & Ads</h1>
          <p className="text-sm text-muted-foreground mt-1">Daily SEO articles, Shopify publishing and Google + Meta campaigns — all built from your keyword research.</p>
        </div>
        {active && <a href={`/b/${active.id}`} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1.5 hover:underline" data-testid="hub-public-blog-link"><Globe className="h-3.5 w-3.5" /> Your public blog page <ExternalLink className="h-3 w-3" /></a>}
      </div>
      <Tabs defaultValue="campaigns">
        <TabsList className="bg-secondary/40">
          <TabsTrigger value="campaigns" data-testid="hub-tab-campaigns">Blog campaigns</TabsTrigger>
          <TabsTrigger value="articles" data-testid="hub-tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="ppc" data-testid="hub-tab-ppc">PPC campaigns</TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns" className="space-y-3">
          {camps === null ? <Loader2 className="h-5 w-5 animate-spin" /> : camps.length === 0 ? <Empty text="No blog campaigns yet. Pick keywords and start one." />
            : camps.map((c) => <CampaignCard key={c.id} c={c} expanded={expanded === c.id} onToggle={() => setExpanded(expanded === c.id ? null : c.id)} onChanged={load} onOpen={setOpen} />)}
        </TabsContent>
        <TabsContent value="articles">
          {items.length === 0 ? <Empty text="No standalone articles yet." /> : <div className="rounded-xl border border-border bg-card overflow-hidden">{items.map((it) => <ItemRow key={it.id} it={it} onOpen={setOpen} />)}</div>}
        </TabsContent>
        <TabsContent value="ppc" className="space-y-3">
          {ppcs.length === 0 ? <Empty text="No PPC campaigns yet." /> : ppcs.map((p) => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold">{p.keywords.slice(0, 4).join(", ")}{p.keywords.length > 4 ? ` +${p.keywords.length - 4}` : ""} <span className="text-xs text-muted-foreground font-normal">· {fmtDate(p.created_at)} · {p.final_url}</span></p>
              <PPCPlanView doc={p} />
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <ItemDialog item={open && open.format_type !== "ppc_ad" ? open : null} onClose={() => { setOpen(null); load(); }} shopifyConnected={defaults?.shopify_connected} />
    </div>
  );
}
